import { useState, useCallback } from "react";
import PilotHeader from "./components/PilotHeader";
import PilotSidebarPanel from "./components/PilotSidebarPanel";
import PilotMapPanel from "./components/PilotMapPanel";
import PilotIndicators from "./components/PilotIndicators";
import PilotBlocksTable from "./components/PilotBlocksTable";
import PilotTreeList from "./components/PilotTreeList";
import {
  areaPiloto,
  quadras,
  arvores,
  indicadoresPiloto,
  areasVerdesEntorno,
  camadasMapaPiloto,
} from "@/mocks/piloto-sbv";
import type { ArvorePiloto, LayerToggle } from "@/types/piloto";

export default function PilotoSBVPage() {
  const [activeLayers, setActiveLayers] = useState<Set<string>>(
    new Set(camadasMapaPiloto.filter((c) => c.ativa).map((c) => c.id))
  );
  const [selectedTree, setSelectedTree] = useState<ArvorePiloto | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleToggleLayer = useCallback((id: string) => {
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectTree = useCallback((tree: ArvorePiloto) => {
    setSelectedTree((prev) => (prev?.id === tree.id ? null : tree));
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] overflow-hidden bg-background-50">
      {/* Header */}
      <PilotHeader area={areaPiloto} />

      {/* Indicators row */}
      <div className="px-4 py-3 shrink-0">
        <PilotIndicators indicadores={indicadoresPiloto} />
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden px-4 pb-4 gap-3">
        {/* Left sidebar */}
        <div className={`shrink-0 transition-all duration-300 ${sidebarCollapsed ? "w-0 overflow-hidden" : "w-[300px]"}`}>
          <PilotSidebarPanel
            area={areaPiloto}
            camadas={camadasMapaPiloto}
            areasVerdes={areasVerdesEntorno}
            activeLayers={activeLayers}
            onToggleLayer={handleToggleLayer}
          />
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="shrink-0 w-5 h-full bg-white border border-background-200/70 rounded-card flex items-center justify-center hover:bg-background-100 transition-colors self-stretch"
          title={sidebarCollapsed ? "Expandir painel" : "Recolher painel"}
        >
          <i className={sidebarCollapsed ? "ri-arrow-right-s-line text-foreground-400 text-xs" : "ri-arrow-left-s-line text-foreground-400 text-xs"}></i>
        </button>

        {/* Center area */}
        <div className="flex-1 flex flex-col gap-3 min-w-0 overflow-hidden">
          {/* Map */}
          <div className="flex-1 min-h-0">
            <PilotMapPanel
              arvores={arvores}
              quadras={quadras}
              camadas={camadasMapaPiloto}
              activeLayers={activeLayers}
              selectedTree={selectedTree}
              onSelectTree={handleSelectTree}
            />
          </div>

          {/* Bottom panels */}
          <div className="flex flex-col lg:flex-row gap-3 shrink-0 h-[280px]">
            <div className="flex-1 min-w-0 overflow-hidden">
              <PilotBlocksTable quadras={quadras} />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <PilotTreeList arvores={arvores} onSelectTree={handleSelectTree} />
            </div>
          </div>
        </div>
      </div>

      {/* Selected tree detail bar */}
      {selectedTree && (
        <div className="shrink-0 bg-white border-t border-background-200/70 px-4 py-2.5 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-plant-line text-primary-600 text-xs"></i>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground-800">
                {selectedTree.especiePopular || "Árvore não identificada"} — {selectedTree.codigo}
              </p>
              <p className="text-[10px] text-foreground-500">
                {selectedTree.quadraCodigo} • {selectedTree.logradouro || "Sem endereço"} • DAP {selectedTree.dapCm}cm
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              selectedTree.risco === "baixo" ? "bg-primary-100 text-primary-700" :
              selectedTree.risco === "medio" ? "bg-caution-100 text-caution-700" :
              selectedTree.risco === "alto" ? "bg-warning-100 text-warning-700" :
              "bg-danger-100 text-danger-700"
            }`}>
              Risco: {selectedTree.risco}
            </span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              selectedTree.estadoFitossanitario === "saudavel" ? "bg-primary-100 text-primary-700" :
              selectedTree.estadoFitossanitario === "regular" ? "bg-caution-100 text-caution-700" :
              selectedTree.estadoFitossanitario === "comprometida" ? "bg-warning-100 text-warning-700" :
              selectedTree.estadoFitossanitario === "doente" ? "bg-orange-100 text-orange-700" :
              "bg-danger-100 text-danger-700"
            }`}>
              {selectedTree.estadoFitossanitario === "saudavel" ? "Saudável" :
               selectedTree.estadoFitossanitario === "regular" ? "Regular" :
               selectedTree.estadoFitossanitario === "comprometida" ? "Comprometida" :
               selectedTree.estadoFitossanitario === "doente" ? "Doente" : "Morta"}
            </span>
            <button
              onClick={() => setSelectedTree(null)}
              className="w-6 h-6 rounded-md hover:bg-background-100 flex items-center justify-center text-foreground-400 transition-colors"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}